import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Container from '../components/shared/Container';
import './IntakePage.css';

/* ─────────────────────────────────────────────
   SHARED
   ───────────────────────────────────────────── */
const DocIcon = () => (
  <svg width="12" height="14" viewBox="0 0 12 14" fill="none" className="flex-shrink-0">
    <path d="M1.5 0h6l3.5 3.5V13a1 1 0 01-1 1h-8.5a1 1 0 01-1-1V1a1 1 0 011-1z"
      fill="#f3f4f6" stroke="#e5e7eb" strokeWidth="0.7"/>
    <path d="M7 0v3.5h3.5" fill="none" stroke="#e5e7eb" strokeWidth="0.7"/>
  </svg>
);

/* ─────────────────────────────────────────────
   CLASSIFY ANIMATION
   Documents arrive → each gets a type badge
   ───────────────────────────────────────────── */
const CLASSIFY_DOCS = [
  { name: 'application_4521.pdf',      type: 'Application',      color: '#60a5fa', bg: '#eff6ff' },
  { name: 'contract_0281.pdf',         type: 'Contract',          color: '#818cf8', bg: '#eef2ff' },
  { name: 'supporting_rec_0092.pdf',   type: 'Supporting Record', color: '#34d399', bg: '#f0fdf4' },
  { name: 'correspondence_0047.pdf',   type: 'Correspondence',    color: '#f59e0b', bg: '#fffbeb' },
];

const ClassifyAnimation = () => {
  const [step, setStep]         = useState(0);
  const [isOut, setIsOut]       = useState(false);
  const [cycleKey, setCycleKey] = useState(0);
  const ref = useRef([]);
  const clr  = () => { ref.current.forEach(clearTimeout); ref.current = []; };
  const after = (fn, ms) => { const t = setTimeout(fn, ms); ref.current.push(t); };

  useEffect(() => {
    clr(); setStep(0); setIsOut(false);
    CLASSIFY_DOCS.forEach((_, i) => after(() => setStep(i + 1), 500 + i * 700));
    after(() => { setIsOut(true); after(() => setCycleKey(k => k + 1), 500); },
      500 + CLASSIFY_DOCS.length * 700 + 2400);
    return clr;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleKey]);

  return (
    <div className="rounded-2xl border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.04),0_8px_32px_rgba(0,0,0,0.08)] bg-white overflow-hidden"
      style={{ opacity: isOut ? 0 : 1, transition: 'opacity 0.45s ease' }}>

      <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-green-400" />
          <span className="text-sm font-semibold text-gray-900">Vermillion Intake</span>
          <span className="text-[0.62rem] text-gray-400 font-medium ml-1">Classifier</span>
        </div>
        <span className="text-[0.62rem] text-gray-400">{step} / {CLASSIFY_DOCS.length} classified</span>
      </div>

      <div className="divide-y divide-gray-50">
        {CLASSIFY_DOCS.map((doc, i) => {
          const done = step > i;
          return (
            <div key={doc.name} className="flex items-center justify-between gap-3 px-5 py-3.5">
              <div className="flex items-center gap-2.5 min-w-0">
                <DocIcon />
                <span className="text-[0.78rem] text-gray-600 truncate">{doc.name}</span>
              </div>
              <div className="flex-shrink-0 w-32 flex justify-end">
                {done ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[0.62rem] font-semibold whitespace-nowrap"
                    style={{ color: doc.color, background: doc.bg, animation: 'ipTagIn 0.35s cubic-bezier(0.34,1.56,0.64,1) both' }}>
                    {doc.type}
                  </span>
                ) : (
                  <span className="flex items-center gap-1">
                    {[0, 1, 2].map(j => (
                      <span key={j} className="w-1 h-1 rounded-full bg-gray-200"
                        style={{ animation: `ipDotPulse 1.2s ${j * 0.2}s ease-in-out infinite` }} />
                    ))}
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   EXTRACT ANIMATION
   Document lines highlight → structured fields appear
   ───────────────────────────────────────────── */
const EXTRACT_FIELDS = [
  { label: 'Applicant',  value: 'J. Williams',         lines: [1, 2] },
  { label: 'Request Type', value: 'Benefits Enrollment', lines: [3] },
  { label: 'Filed',      value: 'Feb 14, 2026',         lines: [5] },
  { label: 'Amount',     value: '$4,200 / mo',          lines: [7] },
  { label: 'Organization', value: 'Acme Corp',          lines: [8, 9] },
];
const LINE_WIDTHS = [85, 40, 70, 50, 60, 45, 78, 55, 65, 35];

const ExtractAnimation = () => {
  const [step, setStep]         = useState(0);
  const [isOut, setIsOut]       = useState(false);
  const [cycleKey, setCycleKey] = useState(0);
  const ref = useRef([]);
  const clr  = () => { ref.current.forEach(clearTimeout); ref.current = []; };
  const after = (fn, ms) => { const t = setTimeout(fn, ms); ref.current.push(t); };

  useEffect(() => {
    clr(); setStep(0); setIsOut(false);
    EXTRACT_FIELDS.forEach((_, i) => after(() => setStep(i + 1), 600 + i * 750));
    after(() => { setIsOut(true); after(() => setCycleKey(k => k + 1), 500); },
      600 + EXTRACT_FIELDS.length * 750 + 2400);
    return clr;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleKey]);

  const hlSet = new Set(EXTRACT_FIELDS.slice(0, step).flatMap(f => f.lines));

  return (
    <div className="rounded-2xl border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.04),0_8px_32px_rgba(0,0,0,0.08)] bg-white overflow-hidden"
      style={{ opacity: isOut ? 0 : 1, transition: 'opacity 0.45s ease', height: '270px' }}>

      <div className="flex divide-x divide-gray-100 h-full">

        {/* Document */}
        <div className="flex-1 p-6">
          <p className="text-[0.6rem] font-bold tracking-[0.1em] uppercase text-gray-400 mb-4">Source Document</p>
          <div className="space-y-[11px]">
            {LINE_WIDTHS.map((w, i) => (
              <div key={i} className="h-[7px] rounded-full transition-all duration-500"
                style={{
                  width: `${w}%`,
                  background: hlSet.has(i) ? '#818cf8' : 'rgba(0,0,0,0.07)',
                  boxShadow: hlSet.has(i) ? '0 0 8px rgba(129,140,248,0.3)' : 'none',
                }} />
            ))}
          </div>
        </div>

        {/* Arrow */}
        <div className="flex items-center px-3 bg-gray-50/60">
          <div style={{
            background: '#fff', borderRadius: '50%', padding: '6px',
            border: `1px solid ${step > 0 ? '#a5b4fc' : '#e5e7eb'}`,
            boxShadow: step > 0 ? '0 0 0 3px #eef2ff' : 'none',
            transition: 'all 0.3s',
          }}>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M2 7h10M8 3l4 4-4 4"
                stroke={step > 0 ? '#818cf8' : '#d1d5db'} strokeWidth="1.5"
                strokeLinecap="round" strokeLinejoin="round"
                style={{ transition: 'stroke 0.3s' }}/>
            </svg>
          </div>
        </div>

        {/* Extracted fields */}
        <div className="flex-1 p-6 bg-slate-50">
          <p className="text-[0.6rem] font-bold tracking-[0.1em] uppercase text-gray-400 mb-4">Extracted</p>
          <div className="space-y-3.5">
            {EXTRACT_FIELDS.map((f, i) => step > i && (
              <div key={f.label} style={{ animation: 'ipFieldIn 0.3s ease both' }}>
                <div className="text-[0.55rem] font-semibold text-gray-400 uppercase tracking-[0.08em] leading-none mb-0.5">
                  {f.label}
                </div>
                <div className="text-[0.78rem] font-semibold flex items-center gap-1"
                  style={{ color: f.accent || '#374151' }}>
                  {f.accent && <span className="inline-block w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: f.accent }} />}
                  {f.value}
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   REVIEW ANIMATION
   Confidence scores → uncertain fields flagged → human approves
   ───────────────────────────────────────────── */
const REVIEW_FIELDS = [
  { label: 'Applicant',  value: 'J. Williams',  conf: 99 },
  { label: 'Filed Date', value: 'Feb 14, 2026', conf: 97 },
  { label: 'Amount',     value: '$4,200',        conf: 71, review: true },
  { label: 'Category',   value: 'B-204',        conf: 68, review: true },
];

const CheckSVG = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none"
    style={{ animation: 'ipCheckIn 0.3s cubic-bezier(0.34,1.56,0.64,1) both' }}>
    <circle cx="7" cy="7" r="6.5" fill="#f0fdf4" stroke="#4ade80" strokeWidth="1"/>
    <path d="M3.5 7l2.5 2.5 4.5-4.5" stroke="#059669" strokeWidth="1.4"
      strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const WarnSVG = () => (
  <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
    <circle cx="7" cy="7" r="6.5" fill="#fffbeb" stroke="#f59e0b" strokeWidth="1"/>
    <path d="M7 4v3.5" stroke="#d97706" strokeWidth="1.5" strokeLinecap="round"/>
    <circle cx="7" cy="10" r="0.75" fill="#d97706"/>
  </svg>
);

const ReviewAnimation = () => {
  const [step, setStep]         = useState(0);
  const [approved, setApproved] = useState(false);
  const [isOut, setIsOut]       = useState(false);
  const [cycleKey, setCycleKey] = useState(0);
  const ref = useRef([]);
  const clr  = () => { ref.current.forEach(clearTimeout); ref.current = []; };
  const after = (fn, ms) => { const t = setTimeout(fn, ms); ref.current.push(t); };

  useEffect(() => {
    clr(); setStep(0); setApproved(false); setIsOut(false);
    REVIEW_FIELDS.forEach((_, i) => after(() => setStep(i + 1), 400 + i * 600));
    const endT = 400 + REVIEW_FIELDS.length * 600;
    after(() => setApproved(true), endT + 800);
    after(() => { setIsOut(true); after(() => setCycleKey(k => k + 1), 500); }, endT + 3200);
    return clr;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleKey]);

  const pendingCount = REVIEW_FIELDS.filter(f => f.review).length;

  return (
    <div className="rounded-2xl border border-gray-100 shadow-[0_2px_8px_rgba(0,0,0,0.04),0_8px_32px_rgba(0,0,0,0.08)] bg-white overflow-hidden"
      style={{ opacity: isOut ? 0 : 1, transition: 'opacity 0.45s ease', height: '300px' }}>

      <div className="px-5 py-3.5 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full transition-colors duration-500"
            style={{ background: approved ? '#4ade80' : '#f59e0b' }} />
          <span className="text-sm font-semibold text-gray-900">Vermillion Intake</span>
          <span className="text-[0.62rem] text-gray-400 font-medium ml-1">Confidence Review</span>
        </div>
        {step >= REVIEW_FIELDS.length && (
          <span className="text-[0.62rem] font-semibold rounded-full px-2 py-0.5 border"
            style={{
              color: approved ? '#059669' : '#d97706',
              background: approved ? '#f0fdf4' : '#fffbeb',
              borderColor: approved ? '#bbf7d0' : '#fde68a',
              animation: 'ipFadeIn 0.3s ease both',
              transition: 'color 0.4s, background 0.4s, border-color 0.4s',
            }}>
            {approved ? 'All verified' : `${pendingCount} needs review`}
          </span>
        )}
      </div>

      <div className="px-5 py-4 space-y-2">
        {REVIEW_FIELDS.map((f, i) => {
          if (step <= i) return null;
          const isReview = f.review;
          const isDone   = approved && isReview;
          const confColor = isDone ? '#4ade80' : f.conf >= 90 ? '#4ade80' : f.conf >= 80 ? '#60a5fa' : '#f59e0b';

          return (
            <div key={f.label}
              className="flex items-center gap-3 rounded-lg px-3 py-2.5"
              style={{
                background:   isDone ? '#f0fdf4' : isReview ? '#fffbeb' : '#f9fafb',
                border: `1px solid ${isDone ? '#bbf7d0' : isReview ? '#fde68a' : '#f3f4f6'}`,
                animation: 'ipFieldIn 0.3s ease both',
                transition: 'background 0.4s, border-color 0.4s',
              }}>
              <div className="w-1.5 h-1.5 rounded-full flex-shrink-0"
                style={{ background: confColor, transition: 'background 0.4s' }} />
              <div className="flex-1 min-w-0">
                <span className="text-[0.62rem] text-gray-400 mr-2">{f.label}</span>
                <span className="text-[0.78rem] font-medium text-gray-700">{f.value}</span>
              </div>
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <span className="text-[0.62rem] font-bold tabular-nums"
                  style={{ color: isDone ? '#059669' : confColor, transition: 'color 0.4s' }}>
                  {f.conf}%
                </span>
                {isDone ? <CheckSVG /> : isReview ? <WarnSVG /> : <CheckSVG />}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/* ─────────────────────────────────────────────
   PAGE
   ───────────────────────────────────────────── */
const pillars = [
  {
    num: '01',
    title: 'Classify',
    description: 'Every document is identified the moment it arrives — applications, contracts, supporting records, correspondence. No manual sorting, no routing rules to maintain.',
  },
  {
    num: '02',
    title: 'Extract',
    description: 'Structured fields are pulled from every document format, including handwritten content and scanned PDFs. Every value is linked back to its source location.',
  },
  {
    num: '03',
    title: 'Verify',
    description: 'Fields below a confidence threshold are flagged and routed for human review. Not skipped, not guessed. High-confidence fields are delivered immediately.',
  },
  {
    num: '04',
    title: 'Monitor',
    description: 'Accuracy and recall are tracked continuously — by form type, by field. You see exactly where the system performs, where it improves, and where human review is focused.',
  },
];

const IntakePage = () => (
  <>
    {/* ── Hero ── */}
    <section className="pt-28 pb-20 sm:pt-32 sm:pb-24 bg-gray-950 border-b border-white/[0.06]">
      <Container>
        <div className="lg:grid lg:grid-cols-12 lg:gap-16 lg:items-center">

          {/* Text */}
          <div className="lg:col-span-6">
            <p className="text-xs font-semibold tracking-[0.12em] uppercase text-vermillion-600 mb-5">
              Document Intelligence
            </p>
            <h1 className="text-[2.75rem] sm:text-5xl lg:text-[3.5rem] font-bold text-white tracking-tight leading-[1.05]">
              Vermillion Intake
            </h1>
            <p className="mt-4 text-xl font-medium text-vermillion-500 italic">
              From any document to trusted data.
            </p>
            <p className="mt-6 text-[1.0625rem] leading-[1.75] text-zinc-400 max-w-lg">
              Documents arrive in every format: typed forms, handwritten records, multi-page files. Vermillion Intake classifies, extracts, and verifies each one with accuracy that holds over time, not just at deployment.
            </p>
            <div className="mt-8 flex items-center gap-5">
              <Link
                to="/request-demo"
                className="inline-flex items-center justify-center py-2.5 px-5 text-sm font-semibold rounded-lg text-white bg-vermillion-600 hover:bg-vermillion-700 transition-colors"
              >
                Request Demo
              </Link>
              <a
                href="#how-it-works"
                onClick={(e) => { e.preventDefault(); document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'instant' }); }}
                className="text-sm font-medium text-zinc-500 hover:text-white transition-colors"
              >
                How it works ↓
              </a>
            </div>
          </div>

          {/* Static card */}
          <div className="hidden lg:block lg:col-span-6 mt-12 lg:mt-0">
            <div className="rounded-2xl border border-white/[0.07] bg-white/[0.03] p-6">

              <p className="text-[0.6rem] font-bold tracking-[0.12em] uppercase text-zinc-600 mb-3">Incoming Queue</p>
              <div className="rounded-lg border border-white/[0.06] bg-white/[0.04] overflow-hidden mb-4">
                {[
                  { name: 'application_4521.pdf',      type: 'Application',      color: '#60a5fa', bg: 'rgba(96,165,250,0.15)' },
                  { name: 'supporting_rec_0092.pdf',   type: 'Supporting Record', color: '#34d399', bg: 'rgba(52,211,153,0.15)' },
                  { name: 'contract_0281.pdf',         type: 'Contract',          color: '#818cf8', bg: 'rgba(129,140,248,0.15)' },
                ].map(doc => (
                  <div key={doc.name} className="flex items-center justify-between px-4 py-2.5 border-b border-white/[0.04] last:border-0">
                    <span className="text-[0.72rem] text-zinc-400 truncate">{doc.name}</span>
                    <span className="text-[0.6rem] font-semibold px-2 py-0.5 rounded-full ml-3 flex-shrink-0"
                      style={{ color: doc.color, background: doc.bg }}>
                      {doc.type}
                    </span>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-2 mb-4">
                <div className="h-px flex-1 bg-white/[0.06]" />
                <span className="text-[0.6rem] font-semibold tracking-[0.1em] uppercase text-vermillion-600 px-2">Vermillion Intake extracts</span>
                <div className="h-px flex-1 bg-white/[0.06]" />
              </div>

              <p className="text-[0.6rem] font-bold tracking-[0.12em] uppercase text-zinc-600 mb-3">Extracted · application_4521.pdf</p>
              <div className="rounded-lg border border-white/[0.06] bg-white/[0.04] px-4 py-3.5 grid grid-cols-2 gap-x-4 gap-y-2.5">
                {[
                  { label: 'Applicant',  value: 'J. Williams' },
                  { label: 'Filed',      value: 'Feb 14, 2026' },
                  { label: 'Request Type', value: 'Enrollment' },
                  { label: 'Amount',     value: '$4,200 / mo' },
                  { label: 'Organization', value: 'Acme Corp' },
                  { label: 'Category',   value: 'B-204' },
                ].map(f => (
                  <div key={f.label}>
                    <div className="text-[0.55rem] text-zinc-600 uppercase tracking-[0.08em] mb-0.5">{f.label}</div>
                    <div className="text-[0.72rem] font-semibold text-zinc-300">{f.value}</div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex items-center gap-2">
                <div className="w-1.5 h-1.5 rounded-full bg-green-400" />
                <span className="text-[0.68rem] text-zinc-500">98.4% field accuracy · 2 fields queued for review</span>
              </div>

            </div>
          </div>

        </div>
      </Container>
    </section>

    {/* ── Three pillars ── */}
    <section id="how-it-works" className="py-16 sm:py-20 bg-gray-50">
      <Container>
        <div className="text-center mb-12">
          <p className="text-xs font-semibold tracking-[0.12em] uppercase text-vermillion-600 mb-3">
            How it works
          </p>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
            Four stages. Zero guesses.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {pillars.map(p => (
            <div key={p.num} className="bg-white rounded-2xl border border-gray-100 p-7 shadow-[0_2px_8px_rgba(0,0,0,0.03)]">
              <p className="text-xs font-bold text-gray-300 tracking-[0.1em] mb-4">{p.num}</p>
              <h3 className="text-lg font-bold text-gray-900 mb-3">{p.title}</h3>
              <p className="text-sm text-gray-500 leading-relaxed">{p.description}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>

    {/* ── Classify ── */}
    <section className="py-20 sm:py-24 bg-white">
      <Container>
        <div className="lg:grid lg:grid-cols-12 lg:gap-16 lg:items-center">
          <div className="lg:col-span-5">
            <p className="text-xs font-semibold tracking-[0.12em] uppercase text-vermillion-600 mb-3">01 / Classify</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
              Every document sorted<br />the moment it arrives
            </h2>
            <p className="mt-5 text-base text-gray-500 leading-relaxed">
              Vermillion Intake identifies every incoming document automatically — applications, contracts, supporting records, correspondence, and more. No manual sorting, no routing rules to maintain, no delays while someone decides where it goes.
            </p>
            <p className="mt-4 text-base text-gray-500 leading-relaxed">
              Classification runs on arrival. By the time a reviewer opens a document, its type is already known.
            </p>
          </div>
          <div className="mt-10 lg:mt-0 lg:col-span-7">
            <ClassifyAnimation />
          </div>
        </div>
      </Container>
    </section>

    {/* ── Extract ── */}
    <section className="py-20 sm:py-24 bg-gray-50">
      <Container>
        <div className="lg:grid lg:grid-cols-12 lg:gap-16 lg:items-center">
          <div className="lg:col-span-7 lg:order-1">
            <ExtractAnimation />
          </div>
          <div className="mt-10 lg:mt-0 lg:col-span-5 lg:order-2">
            <p className="text-xs font-semibold tracking-[0.12em] uppercase text-vermillion-600 mb-3">02 / Extract</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
              Structured fields<br />from any format
            </h2>
            <p className="mt-5 text-base text-gray-500 leading-relaxed">
              Vermillion Intake extracts structured fields from typed PDFs, scanned images, and handwritten records. Each extracted value is linked back to its source location in the document so every result is verifiable, not just accurate.
            </p>
            <p className="mt-4 text-base text-gray-500 leading-relaxed">
              Field accuracy is monitored continuously with client-visible reports. If accuracy drifts, you see it before it compounds.
            </p>
          </div>
        </div>
      </Container>
    </section>

    {/* ── Verify ── */}
    <section className="py-20 sm:py-24 bg-white">
      <Container>
        <div className="lg:grid lg:grid-cols-12 lg:gap-16 lg:items-center">
          <div className="lg:col-span-5">
            <p className="text-xs font-semibold tracking-[0.12em] uppercase text-vermillion-600 mb-3">03 / Verify</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
              Uncertainty handled,<br />not hidden
            </h2>
            <p className="mt-5 text-base text-gray-500 leading-relaxed">
              Every extraction carries a confidence score. Fields above the threshold are delivered immediately. Fields below are flagged and routed to a human reviewer — not skipped, not guessed.
            </p>
            <p className="mt-4 text-base text-gray-500 leading-relaxed">
              This is what makes 98%+ accuracy hold over time: the system knows what it doesn't know, and routes accordingly.
            </p>
          </div>
          <div className="mt-10 lg:mt-0 lg:col-span-7">
            <ReviewAnimation />
          </div>
        </div>
      </Container>
    </section>

    {/* ── Monitor ── */}
    <section className="py-20 sm:py-24 bg-gray-50">
      <Container>
        <div className="lg:grid lg:grid-cols-12 lg:gap-16 lg:items-start">
          {/* Left: partial report card */}
          <div className="lg:col-span-7 lg:order-1">

            <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              {/* Card header */}
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <span className="text-sm font-semibold text-gray-800">Field Analytics Report</span>
                <span className="text-xs text-gray-400">Feb 1 – Feb 21, 2025</span>
              </div>

              {/* Summary stats */}
              <div className="grid grid-cols-3 divide-x divide-gray-100 border-b border-gray-100">
                {[
                  { label: 'Overall Accuracy', value: '98.4%', target: 'Target 98%', up: true },
                  { label: 'Overall Recall',   value: '94.1%', target: 'Target 80%', up: true },
                  { label: 'Forms Processed',  value: '312',   target: 'This period', up: null },
                ].map(({ label, value, target, up }) => (
                  <div key={label} className="px-3 py-3 sm:px-4 sm:py-4">
                    <p className="text-[10px] sm:text-[11px] text-gray-400 mb-1 leading-tight">{label}</p>
                    <p className="text-lg sm:text-xl font-bold text-gray-900">{value}</p>
                    <p className={`text-[11px] mt-0.5 ${up === true ? 'text-emerald-600' : 'text-gray-400'}`}>{target}</p>
                  </div>
                ))}
              </div>

              {/* Per-form table — fades out at bottom */}
              <div className="relative overflow-x-auto">
                <table className="w-full min-w-[420px] text-xs">
                  <thead>
                    <tr className="border-b border-gray-100 bg-gray-50">
                      <th className="px-4 py-2.5 text-left font-medium text-gray-500">Form Type</th>
                      <th className="px-4 py-2.5 text-right font-medium text-gray-500">Accuracy</th>
                      <th className="px-4 py-2.5 text-right font-medium text-gray-500">Recall</th>
                      <th className="px-4 py-2.5 text-right font-medium text-gray-500">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { form: 'Application',       acc: '97.8%', rec: '95.2%', status: 'Complete',  statusColor: 'text-emerald-600 bg-emerald-50' },
                      { form: 'Contract',          acc: '96.1%', rec: '94.8%', status: 'Complete',  statusColor: 'text-emerald-600 bg-emerald-50' },
                      { form: 'Supporting Record', acc: '93.4%', rec: '91.0%', status: 'Partial',   statusColor: 'text-amber-600 bg-amber-50'    },
                      { form: 'Correspondence',    acc: '98.2%', rec: '97.1%', status: 'Complete',  statusColor: 'text-emerald-600 bg-emerald-50' },
                      { form: 'Authorization',     acc: '95.7%', rec: '93.3%', status: 'Complete',  statusColor: 'text-emerald-600 bg-emerald-50' },
                    ].map(({ form, acc, rec, status, statusColor }) => (
                      <tr key={form} className="border-b border-gray-50">
                        <td className="px-4 py-2.5 text-gray-700 font-medium">{form}</td>
                        <td className="px-4 py-2.5 text-right text-gray-600">{acc}</td>
                        <td className="px-4 py-2.5 text-right text-gray-600">{rec}</td>
                        <td className="px-4 py-2.5 text-right">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${statusColor}`}>{status}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Fade mask — partially hides the table rows */}
                <div
                  className="absolute inset-x-0 bottom-0 h-28 pointer-events-none"
                  style={{ background: 'linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.85) 50%, white 100%)' }}
                />
              </div>

              {/* Footer hint */}
              <div className="px-5 py-3 border-t border-gray-100 bg-gray-50 flex items-center gap-2">
                <svg className="w-3.5 h-3.5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-[11px] text-gray-400">Full breakdown available in your client dashboard — field-level detail across all form types.</span>
              </div>
            </div>
          </div>

          {/* Right: text */}
          <div className="mt-10 lg:mt-0 lg:col-span-5 lg:order-2">
            <p className="text-xs font-semibold tracking-[0.12em] uppercase text-vermillion-600 mb-3">04 / Monitor</p>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">
              Accuracy tracked,<br />reported, and yours to see
            </h2>
            <p className="mt-5 text-base text-gray-500 leading-relaxed">
              Every field, every form, every week. Vermillion Intake continuously measures its own accuracy and recall — by form type, by field — and surfaces that data directly to your team.
            </p>
            <p className="mt-4 text-base text-gray-500 leading-relaxed">
              No black box. You see exactly where the system is performing, where it's improving, and where to focus human review.
            </p>
          </div>
        </div>
      </Container>
    </section>

    {/* ── CTA ── */}
    <section className="py-20 bg-gray-950">
      <Container>
        <div className="text-center max-w-xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
            Ready to clear the document backlog?
          </h2>
          <p className="mt-4 text-base text-gray-400 leading-relaxed">
            Most clients are processing live documents within two weeks of kickoff.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link
              to="/request-demo"
              className="inline-flex items-center justify-center py-3 px-7 text-sm font-semibold rounded-lg text-white bg-vermillion-600 hover:bg-vermillion-700 transition-colors"
            >
              Request Demo
            </Link>
            <Link
              to="/"
              className="text-sm font-medium text-gray-500 hover:text-white transition-colors"
            >
              Back to overview →
            </Link>
          </div>
        </div>
      </Container>
    </section>
  </>
);

export default IntakePage;
